import type { MatchState } from '../sim/state';
import type { SimEvent } from '../sim/events';
import type { SimConfig } from '../config/simConfig';
import type { TeamIdx } from '../types';
import { PITCH } from '../config/pitchConfig';
import { goalOver } from '../physics/world';
import { setPhase } from './phase';
import { beginCelebration } from './celebration';

// Goal detection (§6.2). Pure — mutates `state` (score, phase, restart), emits GOAL/WHISTLE. The scoring
// team is the one attacking that goal side (attackDir[t] === side); the scorer is the last toucher, an
// own-goal when that toucher defends. → GOAL_CELEBRATION, then the conceding team kicks off (§6.1).

/** Returns true (and applies the goal) if the ball is fully in a goal this tick. */
export function detectGoal(state: MatchState, events: SimEvent[], cfg: SimConfig): boolean {
  const pitch = PITCH[state.mode.teamSize];
  const side = goalOver(state.ball.pos, pitch, cfg.ball.RADIUS);
  if (side === 0) return false;

  const scoringTeam: TeamIdx = state.attackDir[0] === side ? 0 : 1;
  const concedingTeam: TeamIdx = scoringTeam === 0 ? 1 : 0;
  const lt = state.ball.lastTouch;
  // D48: a LIVE SHOT that goes in belongs to the SHOOTER even off a defender/keeper graze (Laws-of-
  // the-Game attribution — a deflected on-target shot is never an own goal). shotPending survives
  // passive deflections; kickedBy names the shooter. A controlled touch (claim/kick) clears both.
  const shooter = state.ball.shotPending && state.ball.kickedBy !== null ? state.players[state.ball.kickedBy] : undefined;
  const deflectedShot = shooter !== undefined && shooter.team === scoringTeam;
  const scorer = deflectedShot ? shooter.id : lt ? lt.entity : -1;
  const ownGoal = deflectedShot ? false : lt ? lt.team !== scoringTeam : false;

  state.score[scoringTeam] = (state.score[scoringTeam] ?? 0) + 1;
  events.push({ t: 'GOAL', team: scoringTeam, scorer, assist: null, ownGoal });
  events.push({ t: 'WHISTLE', kind: 'GOAL' });
  state.ball.vel = { x: 0, y: 0, z: 0 };
  state.ball.possessor = null;

  // golden goal in overtime ends the match immediately (§6.5)
  if (state.half === 3) {
    events.push({ t: 'WHISTLE', kind: 'FULL_END' });
    setPhase(state, 'FULLTIME', 0, events);
    return true;
  }
  // → GOAL_CELEBRATION; the conceding team's kickoff is armed in `restart` for advancePhase to start (§6.1)
  state.restart = { kind: 'KICKOFF', spot: { x: 0, y: 0 }, team: concedingTeam, taker: null };
  // D108: arm the ceremony. A genuine own goal credits nobody, so there is no scorer to mob and a
  // seeded few of the scoring team celebrate instead. Draws advance state.rng — once, here.
  state.celebration = beginCelebration(state, scoringTeam, ownGoal || scorer < 0 ? null : scorer, cfg);
  setPhase(state, 'GOAL_CELEBRATION', cfg.rules.GOAL_CELEBRATION_TICKS, events);
  return true;
}
